import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  AltklausurQuizCarousel,
  LessonTaskCarousel,
  sortByPositionThenId,
} from "../pages/subjects/student/studentPracticeComponents";
import { normalizeRichHtmlForDisplay, richHtmlToPlainText } from "../utils/richContent";
import { installSavedFormulaRenderer } from "../utils/renderSavedFormulas";
import "../styles/LessonContentTypography.css";
import "../styles/SubjectRightVar1.css";
import "../styles/CheckGtm1.css";

const LESSON_ID = "419d943d-0c77-48ce-8896-96f94196db1e";
const GENERATED_USER_ID = "b1506983-bfb7-4087-a1e6-80e41deb417f";

const ORIGINAL_TASK_IDS = [
  "117e0bf1-ff18-43de-a675-c0e311181300",
  "6f99bfa1-f869-468c-a122-824fac05a0e2",
  "b12895cf-b2ef-4445-ad9e-84ac76af20ba",
  "ee850daa-8879-43b0-b309-11135c8c2e38",
];

const GENERATED_TASK_IDS = [
  "c488d1ca-d402-4f11-8d79-dd5d479e6102",
  "64640b56-16f5-478f-8a32-da22dcdc16da",
  "e1282900-3d78-4621-89e4-4ab90d864996",
  "aa0065b5-6665-4f04-8aad-59c58682b2bb",
];

const EXAM_CONFIGS = [
  {
    key: "staffExam0",
    id: "36669416-508f-4cb2-9623-fd0b7c9eafc0",
    label: "Teaching Staff",
  },
  {
    key: "staffExam1",
    id: "88da8095-3faa-40c3-a757-4c3e16188a68",
    label: "Teaching Staff",
  },
  {
    key: "generatedExam0",
    id: "6e933bec-1610-48eb-a727-195778ed09f5",
    label: "KI-generiert",
  },
];

const GENERATED_TASK_ORIGINS = GENERATED_TASK_IDS.reduce((acc, generatedId, index) => {
  acc[generatedId] = ORIGINAL_TASK_IDS[index] || "";
  return acc;
}, {});

const CHECK_GTM_QUERY = gql`
  query CheckGtmOne {
    subject(id: "385999b9-532a-4526-8ba4-40c86f47a0f2") {
      id
      title
      slug
      description
    }

    originalTask0: lessonTask(id: "117e0bf1-ff18-43de-a675-c0e311181300") {
      ...LessonTaskFields
    }
    originalTask1: lessonTask(id: "6f99bfa1-f869-468c-a122-824fac05a0e2") {
      ...LessonTaskFields
    }
    originalTask2: lessonTask(id: "b12895cf-b2ef-4445-ad9e-84ac76af20ba") {
      ...LessonTaskFields
    }
    originalTask3: lessonTask(id: "ee850daa-8879-43b0-b309-11135c8c2e38") {
      ...LessonTaskFields
    }

    generatedTask0: lessonTask(id: "c488d1ca-d402-4f11-8d79-dd5d479e6102") {
      ...LessonTaskFields
    }
    generatedTask1: lessonTask(id: "64640b56-16f5-478f-8a32-da22dcdc16da") {
      ...LessonTaskFields
    }
    generatedTask2: lessonTask(id: "e1282900-3d78-4621-89e4-4ab90d864996") {
      ...LessonTaskFields
    }
    generatedTask3: lessonTask(id: "aa0065b5-6665-4f04-8aad-59c58682b2bb") {
      ...LessonTaskFields
    }

    staffExam0: altklausur(id: "36669416-508f-4cb2-9623-fd0b7c9eafc0") {
      ...AltklausurFields
    }
    staffExam1: altklausur(id: "88da8095-3faa-40c3-a757-4c3e16188a68") {
      ...AltklausurFields
    }
    generatedExam0: altklausur(id: "6e933bec-1610-48eb-a727-195778ed09f5") {
      ...AltklausurFields
    }
  }

  fragment LessonTaskFields on LessonTask {
    id
    lessonID
    originTaskID
    generatedForUserID
    title
    taskText
    hintText
    answerText
    position
    difficulty
    estimatedMinutes
    sourceType
    isPublished
    createdBy
    updatedBy
    createdAt
    updatedAt
  }

  fragment AltklausurFields on Altklausur {
    id
    subjectID
    title
    description
    isPublished
    sourceType
    generatedForUserId
    createdBy
    versions {
      id
      altklausurID
      title
      versionDate
      description
      sourceType
      timeLimitSeconds
      questionCount
      isPublished
      isLocked
      quizQuestions {
        id
        altklausurVersionID
        position
        questionText
        questionType
        explanation
        hint
        points
        sourceType
        options {
          id
          questionID
          position
          optionText
          isCorrect
        }
      }
      tasks {
        id
        altklausurVersionID
        originTaskID
        generatedForUserID
        title
        taskText
        hintText
        answerText
        position
        difficulty
        estimatedMinutes
        sourceType
        isPublished
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
    }
  }
`;

const formatCount = (count, singular, plural = `${singular}n`) =>
  `${count} ${count === 1 ? singular : plural}`;

const formatMinutes = (seconds) => {
  const value = Number(seconds || 0);
  if (!Number.isFinite(value) || value <= 0) return "";
  const minutes = Math.round(value / 60);
  return `${minutes} min`;
};

const normalizeTask = (task, fallbackOriginTaskID = "") => {
  if (!task?.id) return null;
  const isProvidedGeneratedTask = GENERATED_TASK_IDS.includes(task.id);
  const generatedForUserID =
    task.generatedForUserID || (isProvidedGeneratedTask ? GENERATED_USER_ID : null);
  const sourceType =
    task.sourceType || task.source_type || (generatedForUserID ? "ai" : "tutor");

  return {
    ...task,
    lessonID: task.lessonID || LESSON_ID,
    originTaskID: task.originTaskID || fallbackOriginTaskID || null,
    generatedForUserID,
    sourceType,
    taskText: normalizeRichHtmlForDisplay(task.taskText, "Aufgabentext"),
    hintText: task.hintText ? normalizeRichHtmlForDisplay(task.hintText) : "",
    answerText: task.answerText ? normalizeRichHtmlForDisplay(task.answerText) : "",
  };
};

const normalizeExamTask = (task) => {
  if (!task?.id) return null;
  return {
    ...task,
    taskText: normalizeRichHtmlForDisplay(task.taskText, "Aufgabentext"),
    hintText: task.hintText ? normalizeRichHtmlForDisplay(task.hintText) : "",
    answerText: task.answerText ? normalizeRichHtmlForDisplay(task.answerText) : "",
  };
};

const normalizeQuizQuestion = (question) => {
  if (!question?.id) return null;
  return {
    ...question,
    questionText: normalizeRichHtmlForDisplay(question.questionText, "Prüfungsfrage"),
    options: (question.options || []).slice().sort(sortByPositionThenId),
  };
};

const normalizeVersion = (version) => {
  if (!version?.id) return null;
  return {
    ...version,
    tasks: (version.tasks || [])
      .map(normalizeExamTask)
      .filter(Boolean)
      .sort(sortByPositionThenId),
    quizQuestions: (version.quizQuestions || [])
      .map(normalizeQuizQuestion)
      .filter(Boolean)
      .sort(sortByPositionThenId),
  };
};

const normalizeExam = (exam, config) => {
  if (!exam?.id) return null;
  const versions = (exam.versions || [])
    .map(normalizeVersion)
    .filter(Boolean)
    .sort((a, b) => {
      const dateA = a.versionDate ? new Date(a.versionDate).getTime() : 0;
      const dateB = b.versionDate ? new Date(b.versionDate).getTime() : 0;
      if (dateA !== dateB) return dateB - dateA;
      return String(a.title || "").localeCompare(String(b.title || ""));
    });

  return {
    ...exam,
    displayLabel: config.label,
    versions,
  };
};

const getVersionCounts = (version) => ({
  tasks: version?.tasks?.length || 0,
  quizQuestions: version?.quizQuestions?.length || 0,
});

function CheckGtm1() {
  const rootRef = useRef(null);
  const [view, setView] = useState("klausuren");
  const [selectedExamId, setSelectedExamId] = useState("");
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [examTab, setExamTab] = useState("aufgaben");
  const [activeLessonTaskIndex, setActiveLessonTaskIndex] = useState(0);
  const [activeExamTaskIndex, setActiveExamTaskIndex] = useState(0);
  const [activeExamQuizIndex, setActiveExamQuizIndex] = useState(0);

  const { data, loading, error } = useQuery(CHECK_GTM_QUERY, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  useLayoutEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const pageContent = document.querySelector(".page-content");

    root.classList.add("check-gtm-route-open");
    body.classList.add("check-gtm-route-open");

    if (pageContent) {
      pageContent.style.display = "block";
      pageContent.style.flexDirection = "";
      pageContent.style.minHeight = "0px";
      pageContent.style.height = "auto";
      pageContent.style.overflow = "visible";
    }

    return () => {
      root.classList.remove("check-gtm-route-open");
      body.classList.remove("check-gtm-route-open");

      if (pageContent) {
        pageContent.style.display = "";
        pageContent.style.flexDirection = "";
        pageContent.style.minHeight = "";
        pageContent.style.height = "";
        pageContent.style.overflow = "";
      }
    };
  }, []);

  const lessonTasks = useMemo(() => {
    if (!data) return [];
    const originalTasks = ORIGINAL_TASK_IDS.map((id, index) =>
      normalizeTask(data[`originalTask${index}`], "")
    ).filter(Boolean);
    const generatedTasks = GENERATED_TASK_IDS.map((id, index) =>
      normalizeTask(data[`generatedTask${index}`], GENERATED_TASK_ORIGINS[id])
    ).filter(Boolean);

    return [...originalTasks, ...generatedTasks].sort((a, b) => {
      const aGenerated = GENERATED_TASK_IDS.includes(a.id);
      const bGenerated = GENERATED_TASK_IDS.includes(b.id);
      if (aGenerated !== bGenerated) return aGenerated ? 1 : -1;
      return sortByPositionThenId(a, b);
    });
  }, [data]);

  const exams = useMemo(() => {
    if (!data) return [];
    return EXAM_CONFIGS.map((config) => normalizeExam(data[config.key], config)).filter(Boolean);
  }, [data]);

  const selectedExam = useMemo(
    () => exams.find((exam) => exam.id === selectedExamId) || exams[0] || null,
    [exams, selectedExamId]
  );

  const selectedVersion = useMemo(() => {
    const versions = selectedExam?.versions || [];
    return versions.find((version) => version.id === selectedVersionId) || versions[0] || null;
  }, [selectedExam, selectedVersionId]);

  const selectedCounts = getVersionCounts(selectedVersion);
  const selectedVersionTaskCount = selectedVersion?.tasks?.length || 0;
  const selectedVersionQuizCount = selectedVersion?.quizQuestions?.length || 0;
  const originalTaskCount = lessonTasks.filter((task) => ORIGINAL_TASK_IDS.includes(task.id)).length;
  const generatedTaskCount = lessonTasks.filter((task) => GENERATED_TASK_IDS.includes(task.id)).length;
  const totalExamTasks = exams.reduce(
    (total, exam) =>
      total +
      (exam.versions || []).reduce(
        (versionTotal, version) => versionTotal + (version.tasks?.length || 0),
        0
      ),
    0
  );
  const totalExamQuestions = exams.reduce(
    (total, exam) =>
      total +
      (exam.versions || []).reduce(
        (versionTotal, version) => versionTotal + (version.quizQuestions?.length || 0),
        0
      ),
    0
  );

  useEffect(() => {
    if (!exams.length) return;
    if (!selectedExamId || !exams.some((exam) => exam.id === selectedExamId)) {
      setSelectedExamId(exams[0].id);
    }
  }, [exams, selectedExamId]);

  useEffect(() => {
    const versions = selectedExam?.versions || [];
    if (!versions.length) {
      setSelectedVersionId("");
      return;
    }
    if (!selectedVersionId || !versions.some((version) => version.id === selectedVersionId)) {
      setSelectedVersionId(versions[0].id);
    }
  }, [selectedExam, selectedVersionId]);

  useEffect(() => {
    setActiveExamTaskIndex(0);
    setActiveExamQuizIndex(0);
    const hasTasks = selectedVersionTaskCount > 0;
    const hasQuiz = selectedVersionQuizCount > 0;
    setExamTab(hasTasks || !hasQuiz ? "aufgaben" : "quizfragen");
  }, [selectedVersion?.id, selectedVersionTaskCount, selectedVersionQuizCount]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    return installSavedFormulaRenderer(root);
  }, [data, view, selectedExamId, selectedVersionId, examTab, activeLessonTaskIndex, activeExamTaskIndex, activeExamQuizIndex]);

  const graphQLErrors = error?.graphQLErrors || [];
  const hasPartialError = Boolean(error);
  const subjectTitle = data?.subject?.title || "Grundkurs Technische Mechanik 1";

  const openExam = (examId) => {
    setSelectedExamId(examId);
    setView("klausuren");
  };

  return (
    <main className="check-gtm-page" ref={rootRef}>
      <section className="check-gtm-hero">
        <div>
          <span className="check-gtm-eyebrow">GTM I Prüfungscheck</span>
          <h1>{subjectTitle}</h1>
          <p>
            Read-only Übersicht für die ausgewählten Aufgaben, Musterlösungen,
            generierten Aufgaben und Altklausuren.
          </p>
        </div>

        <div className="check-gtm-stats" aria-label="Inhaltsübersicht">
          <span>
            <strong>{exams.length}</strong>
            Klausuren
          </span>
          <span>
            <strong>{originalTaskCount}</strong>
            Original-Aufgaben
          </span>
          <span>
            <strong>{generatedTaskCount}</strong>
            KI-Aufgaben
          </span>
        </div>
      </section>

      <div className="check-gtm-main-tabs" role="tablist" aria-label="GTM Inhalte">
        <button
          type="button"
          className={view === "klausuren" ? "active" : ""}
          onClick={() => setView("klausuren")}
        >
          Klausuren
        </button>
        <button
          type="button"
          className={view === "aufgaben" ? "active" : ""}
          onClick={() => setView("aufgaben")}
        >
          Aufgaben
        </button>
      </div>

      {hasPartialError ? (
        <section className="check-gtm-warning" role="status">
          <strong>Ein Teil der Daten konnte nicht geladen werden.</strong>
          <span>
            Bitte mit einem Tutor- oder Admin-Account öffnen, der Zugriff auf dieses Fach hat.
            {graphQLErrors[0]?.message ? ` Backend: ${graphQLErrors[0].message}` : ""}
          </span>
        </section>
      ) : null}

      {loading ? (
        <section className="check-gtm-loading">
          <span />
          <span />
          <span />
        </section>
      ) : view === "aufgaben" ? (
        <section className="check-gtm-practice-view">
          <div className="check-gtm-section-head">
            <div>
              <span>Lesson Aufgaben</span>
              <h2>Originale Aufgaben mit generierten Varianten</h2>
              <p>
                {formatCount(originalTaskCount, "Original-Aufgabe", "Original-Aufgaben")} und{" "}
                {formatCount(generatedTaskCount, "KI-Aufgabe", "KI-Aufgaben")} aus der angegebenen
                Lektion.
              </p>
            </div>
          </div>

          <div className="lesson-practice-section check-gtm-practice-section">
            <LessonTaskCarousel
              tasks={lessonTasks}
              activeIndex={activeLessonTaskIndex}
              onActiveIndexChange={setActiveLessonTaskIndex}
              groupGeneratedTasks
              showSidePreviews={false}
            />
          </div>
        </section>
      ) : (
        <section className="check-gtm-board">
          <aside className="check-gtm-exam-list" aria-label="Klausuren">
            <div className="check-gtm-list-head">
              <span>Klausuren</span>
              <strong>{formatCount(exams.length, "Eintrag", "Einträge")}</strong>
            </div>

            <div className="check-gtm-exam-items">
              {exams.length === 0 ? (
                <div className="check-gtm-empty">
                  Keine der angegebenen Klausuren konnte geladen werden.
                </div>
              ) : (
                exams.map((exam, index) => {
                  const version = exam.versions?.[0] || null;
                  const counts = getVersionCounts(version);
                  const isGenerated = exam.generatedForUserId || exam.sourceType === "ai";
                  return (
                    <button
                      key={exam.id}
                      type="button"
                      className={exam.id === selectedExam?.id ? "active" : ""}
                      onClick={() => openExam(exam.id)}
                    >
                      <span className="check-gtm-exam-index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="check-gtm-exam-title">
                        <strong>{exam.title || "Altklausur"}</strong>
                        <small>{isGenerated ? "KI-generierte Klausur" : exam.displayLabel}</small>
                      </span>
                      <span className="check-gtm-exam-counts">
                        {counts.tasks} Afg. · {counts.quizQuestions} Quiz
                      </span>
                      <span className="check-gtm-exam-arrow" aria-hidden="true">›</span>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <section className="check-gtm-detail-panel" aria-label="Ausgewählte Klausur">
            {!selectedExam ? (
              <div className="check-gtm-empty">
                Wähle eine Klausur aus, um Aufgaben und Quizfragen zu sehen.
              </div>
            ) : (
              <>
                <header className="check-gtm-detail-head">
                  <div>
                    <span>{selectedExam.generatedForUserId ? "KI-generierte Klausur" : "Altklausur"}</span>
                    <h2>{selectedExam.title || "Altklausur"}</h2>
                    {selectedExam.description ? <p>{selectedExam.description}</p> : null}
                  </div>
                  <div className="check-gtm-detail-metrics">
                    <span>{formatCount(selectedCounts.tasks, "Aufgabe", "Aufgaben")}</span>
                    <span>{formatCount(selectedCounts.quizQuestions, "Quizfrage", "Quizfragen")}</span>
                    {formatMinutes(selectedVersion?.timeLimitSeconds) ? (
                      <span>{formatMinutes(selectedVersion.timeLimitSeconds)}</span>
                    ) : null}
                  </div>
                </header>

                {selectedExam.versions?.length > 1 ? (
                  <div className="check-gtm-version-strip" aria-label="Version auswählen">
                    {selectedExam.versions.map((version) => (
                      <button
                        key={version.id}
                        type="button"
                        className={version.id === selectedVersion?.id ? "active" : ""}
                        onClick={() => setSelectedVersionId(version.id)}
                      >
                        {version.title || version.versionDate || "Version"}
                      </button>
                    ))}
                  </div>
                ) : null}

                <div className="lesson-practice-tabs check-gtm-content-tabs" role="tablist" aria-label="Klausur Inhalte">
                  <button
                    type="button"
                    className={examTab === "aufgaben" ? "active" : ""}
                    onClick={() => setExamTab("aufgaben")}
                  >
                    Aufgaben
                  </button>
                  <button
                    type="button"
                    className={examTab === "quizfragen" ? "active" : ""}
                    onClick={() => setExamTab("quizfragen")}
                  >
                    Quizfragen
                  </button>
                </div>

                <div className="lesson-practice-section check-gtm-practice-section">
                  {examTab === "aufgaben" ? (
                    selectedVersion?.tasks?.length ? (
                      <LessonTaskCarousel
                        tasks={selectedVersion.tasks}
                        activeIndex={activeExamTaskIndex}
                        onActiveIndexChange={setActiveExamTaskIndex}
                        showSidePreviews={false}
                      />
                    ) : (
                      <div className="practice-empty-state">
                        <div className="practice-empty-title">Keine Aufgaben in dieser Klausur</div>
                        <div className="practice-empty-copy">
                          Diese Version enthält aktuell keine Aufgaben.
                        </div>
                      </div>
                    )
                  ) : selectedVersion?.quizQuestions?.length ? (
                    <AltklausurQuizCarousel
                      questions={selectedVersion.quizQuestions}
                      activeIndex={activeExamQuizIndex}
                      onActiveIndexChange={setActiveExamQuizIndex}
                      compactSidePreviews
                    />
                  ) : (
                    <div className="practice-empty-state">
                      <div className="practice-empty-title">Keine Quizfragen in dieser Klausur</div>
                      <div className="practice-empty-copy">
                        Diese Version enthält aktuell keine Quizfragen.
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </section>
        </section>
      )}

      <section className="check-gtm-footnote">
        <span>{formatCount(totalExamTasks, "Klausur-Aufgabe", "Klausur-Aufgaben")}</span>
        <span>{formatCount(totalExamQuestions, "Klausur-Quizfrage", "Klausur-Quizfragen")}</span>
        <span>{richHtmlToPlainText(data?.subject?.description || "") || "Nur die angegebenen IDs werden geladen."}</span>
      </section>
    </main>
  );
}

export default CheckGtm1;
